import { useCallback } from 'react';
import { bffUpload, legacy } from '@/shared/api/client';
import type { Banner, BannerPaginatedResponse } from '@/shared/types/banner';
import type { DataTableFetchParams, DataTableFetchResult } from '@/shared/components/data-table';
import { mapLegacyBanner, type LegacyBanner } from '../model/banner-adapter';

const BANNER_COLUMNS = [
  { data: 'banner', searchable: 'true' },
  { data: 'title', searchable: 'true' },
  { data: 'description', searchable: 'true' },
  { data: 'entryDate', searchable: 'false' },
  { data: 'exitDate', searchable: 'false' },
  { data: 'active', searchable: 'false' },
  { data: 'order', searchable: 'false' },
];

function buildBody(params: DataTableFetchParams) {
  const order =
    params.sorting.length > 0
      ? params.sorting.map((s) => ({
          column: BANNER_COLUMNS.findIndex((c) => c.data === s.id),
          dir: s.desc ? ('desc' as const) : ('asc' as const),
        }))
      : [{ column: 6, dir: 'asc' as const }];

  return {
    length: params.pageSize,
    skip: params.pageIndex * params.pageSize,
    search: { value: params.search },
    order,
    columns: BANNER_COLUMNS,
  };
}

export function useBannersFetcher(idPassbook: string) {
  return useCallback(
    async (params: DataTableFetchParams): Promise<DataTableFetchResult<Banner>> => {
      const res = await legacy.post<BannerPaginatedResponse>(`/banners/${idPassbook}`, buildBody(params), {
        headers: { 'X-Passbook-Id': idPassbook },
      });
      const rows = (res.data ?? []).map((row) => mapLegacyBanner(row as LegacyBanner));
      return { rows, total: res.filtered ?? 0 };
    },
    [idPassbook],
  );
}

export async function toggleBannerActive(idPassbook: string, bannerId: string, active: boolean) {
  await legacy.patch(`/banners/${bannerId}`, { active }, { headers: { 'X-Passbook-Id': idPassbook } });
}

export async function updateBanner(
  idPassbook: string,
  bannerId: string,
  payload: {
    title?: string;
    description?: string;
    url?: string;
    entryDate?: string | null;
    exitDate?: string | null;
    expiry?: boolean;
    active?: boolean;
  },
) {
  await legacy.patch(`/banners/${bannerId}`, payload, {
    headers: { 'X-Passbook-Id': idPassbook },
  });
}

export async function updateBannerImage(bannerId: string, image: File) {
  const formData = new FormData();
  formData.append('banner', image);
  await bffUpload(`/bff/uploads/banner/${bannerId}/image`, formData);
}

export async function deleteBanner(idPassbook: string, bannerId: string) {
  await legacy.delete(`/banners/${bannerId}?passbookId=${encodeURIComponent(idPassbook)}`, {
    headers: { 'X-Passbook-Id': idPassbook },
  });
}

export async function moveBannerOrder(idPassbook: string, bannerId: string, direction: 'up' | 'down') {
  await legacy.put(
    `/banners/${bannerId}/order`,
    { direction, passbookId: idPassbook },
    { headers: { 'X-Passbook-Id': idPassbook } },
  );
}

/**
 * Reordena banners via drag-and-drop: chama moveBannerOrder em sequência para
 * mover o banner arrastado da posição `from` até a posição `to`.
 */
export async function reorderBanner(
  idPassbook: string,
  bannerId: string,
  from: number,
  to: number,
): Promise<void> {
  const delta = to - from;
  if (delta === 0) return;
  const direction = delta > 0 ? 'down' : 'up';
  const steps = Math.abs(delta);
  for (let i = 0; i < steps; i++) {
    await moveBannerOrder(idPassbook, bannerId, direction);
  }
}
